import React, { useState, useEffect } from 'react';
import {
  Row, Col, Form, Button, Table, Card, Typography,
  Input, notification, Select,
} from 'antd';
import { useNavigate } from 'react-router-dom';
import {
  CloseCircleOutlined,
  EditOutlined,
} from '@ant-design/icons';
import FilterRemoveIcon from 'mdi-react/FilterRemoveIcon';
import FilterIcon from 'mdi-react/FilterIcon';
import PlusIcon from 'mdi-react/PlusIcon';
import {
  get, put, isSuperAgentError, RequestWrapper, ResponseWrapper,
} from '../../../dataSource/requests';
import { getUserType, getUserPermissions } from '../../../utils/users';
import './ListCategories.scss';

const ListCategories = (): React.ReactElement => {
  interface CategoryRecord {
    id: number,
    name: string,
    active: boolean,
  }

  interface TablePagination {
    current: number | undefined,
    defaultCurrent: number | undefined,
    pageSize: number | undefined,
    showQuickJumper: boolean | {
      goButton?: React.ReactNode;
    },
    total: number | undefined,
  }

  const navigate = useNavigate();
  const [formInstance] = Form.useForm();
  const userType = getUserType();
  const userPermissions = getUserPermissions();

  const [deleteLoading, setDeleteLoading] = useState<boolean>(false);
  const [tableLoading, setTableLoading] = useState<boolean>(false);

  const [tablePagination, setTablePagination] = useState<TablePagination>({
    current: 1,
    defaultCurrent: 1,
    pageSize: 15,
    showQuickJumper: true,
    total: 0,
  });

  const [tableDataSource, setTableDataSource] = useState<Array<CategoryRecord>>([]);

  const executeFilter = async (args: {
    page?: number,
    pageSize?: number,
    orderBy?: string,
    orderByDir?: string,
  } = {
    page: 0,
    pageSize: 15,
    orderBy: 'name',
    orderByDir: 'ASC',
  }): Promise<void> => {
    setTableLoading(true);
    let response: ResponseWrapper | undefined;
    let responseError: ResponseWrapper | undefined;
    let genericError: Error | undefined;

    const values = formInstance.getFieldsValue();
    let finalStatus = values.status;

    if (finalStatus === 'ALL') {
      finalStatus = undefined;
    }

    const argsQuery = {
      active: finalStatus,
      name: (values.name != null) ? values.name : undefined,
      orderBy: ('orderBy' in args) ? args.orderBy : 'name',
      page: ('page' in args && args.page >= 1) ? args?.page : 0,
      pageSize: ('pageSize' in args) ? args.pageSize : 15,
      sort: ('orderByDir' in args) ? args.orderByDir : 'ASC',
    };

    const requestReference: RequestWrapper = get({
      url: '/notificationCategory/listAllPageable',

      query: {
        ...Object.fromEntries(
          Object
            .entries(argsQuery)
            .filter(
              ([, elementValue]) => {
                return elementValue != null;
              },
            ),
        ),
      },

      frontConfigs: {
        navigateFunction: navigate,
      },
    });

    try {
      response = await requestReference;
    } catch (error: unknown) {
      console.log('error', error);

      if (isSuperAgentError(error)) {
        responseError = error.response;
      } else {
        genericError = error as Error;
      }
    }

    switch (true) {
      case (response != null && response?.body?.code === 18002): {
        // "Notification category request successfully", 18002
        const pagination = tablePagination;
        pagination.pageSize = response?.body?.paginationInfo?.maxSize;
        pagination.current = response?.body?.paginationInfo?.pageNumber + 1;
        pagination.total = response?.body?.paginationInfo?.totalElements;

        setTableDataSource(response?.body?.listAllNotificationCategoryOutput);

        setTablePagination((previous) => {
          return ({
            ...previous,
            current: pagination.current,
            defaultCurrent: pagination.defaultCurrent,
            pageSize: pagination.pageSize,
            showQuickJumper: pagination.showQuickJumper,
            total: pagination.total,
          });
        });
      } break;

      case (responseError?.body?.code === -4001):
        // "You must be authenticated to perform this operation", -4001
        break;

      case (responseError?.body?.code === -4003):
        // "You do not have permission to perform this operation", -4003
        notification.error({
          message: 'Erro',
          description: 'Você não possui permissão para realizar esta ação',
        });
        break;

      case (genericError?.message?.includes('has been aborted')):
        // dont show anything when request is aborted
        break;

      default:
        notification.error({
          message: 'Erro',
          description: 'Desculpe, houve um erro ao pesquisar as categorias, por favor tente novamente mais tarde.',
        });
        break;
    }

    setTableLoading(false);
  };

  useEffect(() => {
    const executeOnMount = async (): Promise<void> => {
      await executeFilter();
    };

    executeOnMount();
    return () => {
      setDeleteLoading(false);
    };
  }, []);

  const deleteCategory = async (categoryID: number): Promise<void> => {
    let errorFind = false;

    let findData: CategoryRecord = {
      name: '',
      id: 0,
      active: true,
    };

    let responseFind: ResponseWrapper | undefined;
    let responseFindError: ResponseWrapper | undefined;
    let genericErrorFind: Error | undefined;

    let responseDelete: ResponseWrapper | undefined;
    let responseDeleteError: ResponseWrapper | undefined;
    let genericErrorDel: Error | undefined;

    const findRequestReference = get({
      url: `/notificationCategory/findById/${categoryID}`,

      frontConfigs: {
        navigateFunction: navigate,
      },
    });

    try {
      setDeleteLoading(true);
      responseFind = await findRequestReference;
    } catch (error: unknown) {
      console.log('error', error);

      if (isSuperAgentError(error)) {
        responseFindError = error.response;
      } else {
        genericErrorFind = error as Error;
      }
    }

    switch (true) {
      case (responseFind?.body?.code === 18002):
        // "Notification category request successfully", 18002
        findData = responseFind?.body?.notificationCategoryDetaisOutput;
        break;

      case (responseFindError?.body?.code === -4001):
        // "You must be authenticated to perform this operation", -4001
        errorFind = true;
        break;

      case (responseFindError?.body?.code === -4003):
        // "You do not have permission to perform this operation", -4003
        notification.error({
          message: 'Erro',
          description: 'Você não tem permissão para realizar esta ação.',
        });
        errorFind = true;
        break;

      case (responseFindError?.body?.code === -20000):
        // "Notification category not found", -20000
        notification.error({
          message: 'Erro',
          description: 'Categoria não encontrada',
        });
        errorFind = true;
        break;

      case (genericErrorFind?.message?.includes('has been aborted')):
        // dont show anything when request is aborted
        break;

      default:
        notification.error({
          message: 'Erro',
          description: 'Desculpe, houve um erro ao realizar esta ação. Por favor tente novamente mais tarde.',
        });
        errorFind = true;
        break;
    }

    if (errorFind) {
      return;
    }

    const delRequestReference: RequestWrapper = put({
      url: '/notificationCategory/editCategory',

      params: {
        id: categoryID,
        name: findData.name,
        active: false,
      },

      frontConfigs: {
        navigateFunction: navigate,
      },
    });

    try {
      responseDelete = await delRequestReference;
    } catch (error: unknown) {
      console.log('error', error);

      if (isSuperAgentError(error)) {
        responseDeleteError = error.response;
      } else {
        genericErrorDel = error as Error;
      }
    }

    switch (true) {
      case (responseDelete?.body?.code === 18001):
        // "Notification category request successfully", 18001
        notification.success({
          message: 'Sucesso!',
          description: 'Categoria inativada.',
        });

        formInstance.submit();
        break;

      case (responseDeleteError?.body?.code === -4001):
        // "You must be authenticated to perform this operation", -4001
        break;

      case (responseDeleteError?.body?.code === -4003):
        // "You do not have permission to perform this operation", -4003
        notification.error({
          message: 'Erro',
          description: 'Você não possui permissão para realizar esta ação.',
        });
        break;

      case (responseDeleteError?.body?.code === -20000):
        // "Notification category not found", -20000
        notification.error({
          message: 'Erro',
          description: 'Categoria não encontrada.',
        });
        break;

      case (genericErrorDel?.message?.includes('has been aborted')):
        // dont show anything when request is aborted
        break;

      default:
        notification.error({
          message: 'Erro',
          description: 'Desculpe, houve um erro ao realizar esta ação. Por favor tente novamente mais tarde.',
        });
        break;
    }

    setDeleteLoading(false);
  };

  const handleTableChange = (pagination: any, _filters: any, sorter: any): void => {
    setTableLoading(true);

    setTablePagination((previous) => {
      return ({
        ...previous,
        current: pagination.current || previous.current,
        defaultCurrent: pagination.defaultCurrent || previous.defaultCurrent,
        pageSize: pagination.pageSize || previous.pageSize,
        showQuickJumper: pagination.showQuickJumper
          || previous.showQuickJumper,
        total: pagination.total || previous.total,
      });
    });

    let finalOrderDir;

    if (sorter?.field && sorter?.order) {
      switch (true) {
        case (sorter.order === 'descend'):
          finalOrderDir = 'DESC';
          break;

        case (sorter.order === 'ascend'):
        case (sorter.order == null):
        default:
          finalOrderDir = 'ASC';
          break;
      }
    }

    executeFilter({
      page: (pagination?.current)
        ? pagination?.current - 1
        : 0,
      orderBy: sorter?.field || 'name',
      orderByDir: finalOrderDir || 'ASC',
      pageSize: pagination.pageSize,
    });
  };

  return (
    <div
      className="ListCategories"
    >
      <Row
        className="ListCategories__ComponentRow"
        gutter={[24, 24]}
      >
        <Col
          className="ListCategories__ComponentColumn"
          xs={{ span: 24 }}
        >
          <Row
            gutter={[24, 24]}
            justify="space-between"
            className="ListCategories__PageTitleRow"
          >
            <Col
              className="ListCategories__PageTitleColumn"
            >
              <Typography.Title
                className="ListCategories__PageTitle"
              >
                Categorias de notificação
              </Typography.Title>
            </Col>

            <Col
              className="ListCategories__ButtonColumn"
              xs={{ span: 24 }}
              sm={{ span: 24 }}
              md={{ span: 24 }}
              lg={{ span: 12 }}
              xl={{ span: 10 }}
            >
              <Row
                gutter={24}
                justify="end"
                className="ListCategories__NestedButtonRow"
              >
                <Col
                  xs={{ span: 24 }}
                  sm={{ span: 12 }}
                  lg={{ span: 12 }}
                  className="ListCategories__BackButtonColumn"
                >
                  <Button
                    type="default"
                    href="/home"
                    className="ListCategories__BackButton"
                    onClick={
                      (eventt): void => {
                        eventt.preventDefault();
                        navigate('/home');
                      }
                    }
                    block
                  >
                    Voltar
                  </Button>
                </Col>

                {
                  userType === 'COOPERCITRUS'
                    && (userPermissions || []).includes('Solicitar nova categoria')
                    ? (
                      <Col
                        xs={{ span: 24 }}
                        sm={{ span: 12 }}
                        lg={{ span: 12 }}
                        className="ListCategories__NewCategoryButtonColumn"
                      >
                        <Button
                          type="primary"
                          block
                          icon={<PlusIcon className="ListCategories__NewCategoryButtonIcon" size={21} />}
                          href="/notificationcategories/new"
                          className="ListCategories__NewCategoryButton"
                          onClick={
                            (eventt): void => {
                              eventt.preventDefault();
                              navigate('/notificationcategories/new');
                            }
                          }
                        >
                          Cadastrar categoria
                        </Button>
                      </Col>
                    )
                    : null
                }
              </Row>
            </Col>
          </Row>

          <Row
            className="ListCategories__ContentRow"
            gutter={[24, 24]}
          >
            <Col
              xs={{ span: 24 }}
              className="ListCategories__ContentColumn"
            >
              <Row
                gutter={[24, 24]}
                className="ListCategories__FilterRow"
              >
                <Col
                  xs={{ span: 24 }}
                  className="ListCategories__FilterColumn"
                >
                  <Form
                    layout="vertical"
                    className="ListCategories__Form"
                    form={formInstance}
                    onFinish={
                      async (): Promise<void> => {
                        await executeFilter();
                      }
                    }
                    onFinishFailed={
                      (errorInfo): void => {
                        console.log('form finish failed', errorInfo);
                      }
                    }
                  >
                    <Row
                      gutter={[24, 24]}
                      className="ListCategories__NameFieldRow"
                    >
                      <Col
                        xs={{ span: 24 }}
                        sm={{ span: 24 }}
                        md={{ span: 12 }}
                        lg={{ span: 12 }}
                        xl={{ span: 6 }}
                        xxl={{ span: 6 }}
                        className="ListCategories__NameFieldColumn"
                      >
                        <Form.Item
                          className="ListCategories__NameFormField"
                          label={(
                            <Typography.Text
                              className="ListCategories__AllFormLabels ListCategories__NameFieldLabel"
                            >
                              Nome
                            </Typography.Text>
                          )}
                          name="name"
                        >
                          <Input className="ListCategories__NameInput" />
                        </Form.Item>
                      </Col>

                      <Col
                        xs={{ span: 24 }}
                        sm={{ span: 24 }}
                        md={{ span: 12 }}
                        lg={{ span: 12 }}
                        xl={{ span: 6 }}
                        xxl={{ span: 6 }}
                        className="ListCategories__NameFieldColumn"
                      >
                        <Form.Item
                          className="ListCategories__StatusFormField"
                          label={(
                            <Typography.Text
                              className="ListCategories__AllFormLabels ListCategories__StatusFieldLabel"
                            >
                              Status
                            </Typography.Text>
                          )}
                          name="status"
                          initialValue="ALL"
                        >
                          <Select
                            className="ListCategories__StatusSelectInput"
                            options={[
                              {
                                label: 'Ativo',
                                value: true,
                                key: 'status-active-true',
                              },
                              {
                                label: 'Inativo',
                                value: false,
                                key: 'status-active-false',
                              },
                              {
                                label: 'Todos',
                                value: 'ALL',
                                key: 'status-active-all',
                              },
                            ]}
                          />
                        </Form.Item>
                      </Col>

                      <Col
                        className="ListCategories__ResetButtonColumn"
                        xs={{ span: 24 }}
                        sm={{ span: 24 }}
                        md={{ span: 6 }}
                        lg={{ span: 6 }}
                        xl={{ span: 3 }}
                        xxl={{ span: 3 }}
                      >
                        <Form.Item
                          label=" "
                          className="ListCategories__ResetButtonFormItem"
                        >
                          <Button
                            block
                            icon={(
                              <FilterRemoveIcon
                                size={18}
                                className="ListCategories__ResetButtonIcon"
                              />
                            )}
                            className="ListCategories__ResetButton"
                            type="default"
                            htmlType="button"
                            onClick={
                              (): void => {
                                formInstance.resetFields();
                                formInstance.submit();
                              }
                            }
                          >
                            Limpar
                          </Button>
                        </Form.Item>
                      </Col>

                      <Col
                        className="ListCategories__SubmitButtonColumn"
                        xs={{ span: 24 }}
                        sm={{ span: 24 }}
                        md={{ span: 6 }}
                        lg={{ span: 6 }}
                        xl={{ span: 3 }}
                        xxl={{ span: 3 }}
                      >
                        <Form.Item
                          label=" "
                          className="ListCategories__SubmitButtonFormItem"
                        >
                          <Button
                            block
                            type="primary"
                            htmlType="submit"
                            icon={(
                              <FilterIcon
                                size={18}
                                className="ListCategories__SubmitButtonIcon"
                              />
                            )}
                            className="ListCategories__SubmitButton"
                          >
                            Filtrar
                          </Button>
                        </Form.Item>
                      </Col>
                    </Row>
                  </Form>
                </Col>
              </Row>

              <Card
                className="ListCategories__Card"
              >
                <Row
                  gutter={[24, 24]}
                  className="ListCategories__CardContentRow"
                >
                  <Col
                    xs={{ span: 24 }}
                    className="ListCategories__CardContentColumn"
                  >
                    <Table
                      className="ListCategories__Table"
                      columns={[
                        {
                          title: 'ID',
                          ellipsis: true,
                          dataIndex: 'id',
                          sorter: true,
                          key: 'id',
                          className: 'ListCategories__TableTitle',
                        },
                        {
                          title: 'Nome',
                          ellipsis: true,
                          dataIndex: 'name',
                          sorter: true,
                          defaultSortOrder: 'ascend',
                          key: 'name',
                          className: 'ListCategories__TableTitle',
                        },
                        {
                          title: 'Status',
                          ellipsis: true,
                          sorter: true,
                          dataIndex: 'active',
                          key: 'active',
                          className: 'ListCategories__TableTitle',

                          render: (value: string | number | boolean): React.ReactNode => {
                            if (value === true) {
                              return 'Ativo';
                            }

                            return 'Inativo';
                          },
                        },
                        {
                          title: 'Ações',
                          dataIndex: 'id',
                          key: 'actions',
                          className: 'ListCategories__TableTitle',

                          render: (
                            _value: string | number,
                            record: CategoryRecord,
                          ): React.ReactNode => {
                            return (
                              <>
                                {
                                  userType === 'COOPERCITRUS'
                                    && (userPermissions || []).includes('Editar categoria')
                                    ? (
                                      <Button
                                        type="link"
                                        icon={<EditOutlined className="ListCategories__EditButtonIcon" />}
                                        title="Editar categoria"
                                        loading={deleteLoading}
                                        href={`/notificationcategories/${record.id}/edit`}
                                        onClick={
                                          (eventt): void => {
                                            eventt.preventDefault();
                                            navigate(`/notificationcategories/${record.id}/edit`);
                                          }
                                        }
                                        className="ListCategories__EditButton"
                                      />
                                    )
                                    : null
                                }

                                {
                                  userType === 'COOPERCITRUS'
                                    && (userPermissions || []).includes('Inativação de categoria')
                                    ? (
                                      <Button
                                        type="link"
                                        icon={<CloseCircleOutlined className="ListCategories__DeactivateButtonIcon" />}
                                        title="Inativar categoria"
                                        loading={deleteLoading}
                                        onClick={
                                          (): void => {
                                            deleteCategory(record.id);
                                          }
                                        }
                                        className="ListCategories__DeactivateButton"
                                      />
                                    )
                                    : null
                                }
                              </>
                            );
                          },
                        },
                      ]}
                      dataSource={tableDataSource}
                      loading={tableLoading}
                      rowKey={(record): string => record.id as unknown as string}
                      pagination={{
                        defaultCurrent: tablePagination.defaultCurrent || 1,
                        current: tablePagination.current || 1,
                        pageSize: tablePagination.pageSize || 15,
                        total: tablePagination.total || 0,
                        showQuickJumper: tablePagination.showQuickJumper || true,
                        pageSizeOptions: ['1', '15', '50', '100'],
                        showSizeChanger: true,
                      }}
                      onChange={handleTableChange}
                    />
                  </Col>
                </Row>
              </Card>
            </Col>
          </Row>
        </Col>
      </Row>
    </div>
  );
};

ListCategories.propTypes = {};
ListCategories.defaultProps = {};

export default ListCategories;
